/**
 * Produto B2B — "Fisio em Ortopedia para Clínicas".
 *
 * Programa de Capacitação e Atualização Continuada para equipes de
 * fisioterapia musculoesquelética. Venda consultiva: o lead é capturado na LP
 * pública `/feoclinicas`, fica APENAS no banco do Lovable (tabela `b2b_leads`) e é
 * trabalhado no mini-CRM `/admin/feoclinicas` pelo professor responsável.
 *
 * IMPORTANTE: leads B2B não vão para RD Station nem RD CRM.
 */

export type B2bStage =
  | "sem-contato"
  | "contato-1"
  | "contato-2"
  | "contato-3"
  | "em-conversa"
  | "em-negociacao"
  | "oportunidade-futura"
  | "venda"
  | "perda";

export const B2B_STAGES: { id: B2bStage; label: string; color: string }[] = [
  { id: "sem-contato", label: "Sem contato", color: "#8b9bb5" },
  { id: "contato-1", label: "Contato 1", color: "#6c63ff" },
  { id: "contato-2", label: "Contato 2", color: "#3b82f6" },
  { id: "contato-3", label: "Contato 3", color: "#f5b022" },
  { id: "em-conversa", label: "Em conversa", color: "#3fd5c9" },
  { id: "em-negociacao", label: "Em negociação", color: "#a855f7" },
  { id: "oportunidade-futura", label: "Oportunidade futura", color: "#94a3b8" },
  { id: "venda", label: "Venda", color: "#10b981" },
  { id: "perda", label: "Perda", color: "#ef4444" },
];

export const B2B_STAGE_IDS = B2B_STAGES.map((s) => s.id);

export function stageMeta(id: string) {
  return B2B_STAGES.find((s) => s.id === id) ?? B2B_STAGES[0];
}

/** Pergunta 1 — porte da equipe. */
export const NUM_FISIOS_OPTIONS = ["Até 3", "4 a 10", "Mais de 10"] as const;
/** Pergunta 2 — cargo na clínica. */
export const CARGO_OPTIONS = ["Dono/gestor", "Fisioterapeuta", "Outros"] as const;
/** Planos oferecidos nos cards da LP (nomes definitivos). */
export const PLANO_OPTIONS = ["Livre", "Guiado"] as const;
/**
 * Valor gravado no lead: "Livre"/"Guiado" apenas quando a pessoa clicou no CTA
 * de um plano específico; qualquer outro CTA da LP grava "Geral".
 * Leads antigos podem ter "Pro"/"Premium" (nomenclatura anterior).
 */
export const PLANO_LEAD_OPTIONS = [...PLANO_OPTIONS, "Geral"] as const;

export type NumFisios = (typeof NUM_FISIOS_OPTIONS)[number];
export type Cargo = (typeof CARGO_OPTIONS)[number];
export type Plano = (typeof PLANO_OPTIONS)[number];
export type PlanoLead = (typeof PLANO_LEAD_OPTIONS)[number];

/** Rótulo legível de um plano gravado no lead (compatível com nomes antigos). */
export function planoLabel(plano?: string | null) {
  if (!plano) return "Geral";
  if (plano === "Pro") return "Livre (ex-Pro)";
  if (plano === "Premium") return "Guiado (ex-Premium)";
  return plano;
}

/** true quando o plano gravado corresponde ao Plano Guiado (inclui legado). */
export function isPlanoGuiado(plano?: string | null) {
  return plano === "Guiado" || plano === "Premium";
}

export const B2B_PLANOS: {
  id: Plano;
  nome: string;
  /** Valor mensal por fisioterapeuta, cobrado em assinatura anual (12 meses). */
  precoMensalPorFisio: number;
  /** Descrição curta em itálico logo abaixo do nome. */
  ideal: string;
  /** Título da lista de benefícios. */
  listaLabel: string;
  /** Texto do botão. */
  cta: string;
  destaque: boolean;
  itens: string[];
}[] = [
  {
    id: "Livre",
    precoMensalPorFisio: 97,
    nome: "Plano Livre",
    ideal:
      "Para clínicas que querem dar autonomia à equipe com acesso completo às formações da FEO.",
    listaLabel: "Sua equipe terá acesso a:",
    cta: "Quero o Plano Livre",
    destaque: false,
    itens: [
      "6 opções de pós-graduações",
      "+120 cursos e formações",
      "Agente de IA e Roteiro de Avaliação",
      "Discussões Clínicas Ao Vivo",
      "Comunidade FEO",
    ],
  },
  {
    id: "Guiado",
    precoMensalPorFisio: 297,
    nome: "Plano Guiado",
    ideal:
      "Para clínicas que querem direcionar e acompanhar de perto o desenvolvimento de cada fisio.",
    listaLabel: "Inclui tudo do Plano Livre e também:",
    cta: "Quero o Plano Guiado",
    destaque: true,
    itens: [
      "Diagnóstico da clínica",
      "Trilhas individuais de desenvolvimento",
      "Reavaliações periódicas",
    ],
  },
];



/** Duração do contrato (igual nos dois planos). */
export const B2B_DURACAO = "1 ano (12 meses)";

/** Etapas do programa (fonte: material oficial "Passo a passo 2026"). */
export const B2B_ETAPAS: { n: string; titulo: string; desc: string }[] = [
  {
    n: "01",
    titulo: "Diagnóstico do serviço",
    desc: "Análise das características e peculiaridades da clínica junto à gestão, por meio de formulário específico.",
  },
  {
    n: "02",
    titulo: "Avaliação individual de competências",
    desc: "Cada fisioterapeuta responde a uma avaliação clínica que mapeia 5 competências: avaliação fisioterapêutica, raciocínio clínico, embasamento teórico, soft skills e prática baseada em evidências.",
  },
  {
    n: "03",
    titulo: "Apresentação das ações",
    desc: "Relatório geral com planos de ação para a equipe, a partir do diagnóstico e do resultado das avaliações.",
  },
  {
    n: "04",
    titulo: "Plano de estudo personalizado",
    desc: "Plano individual com conteúdos da plataforma indicados por competência, atualizado a cada quatro meses.",
  },
  {
    n: "05",
    titulo: "Engajamento e adequações",
    desc: "Relatório quadrimestral de atividades da equipe: carga horária cumprida, ranking de engajamento e metas.",
  },
  {
    n: "06",
    titulo: "Reavaliação",
    desc: "Nova avaliação de competências a cada quatro meses para redefinir metas e atualizar os planos de estudo.",
  },
];

export const B2B_NUMEROS: { valor: string; label: string }[] = [
  { valor: "+45 mil", label: "alunos ativos" },
  { valor: "+120", label: "formações e cursos" },
  { valor: "+80", label: "professores referência" },
  { valor: "+200", label: "clínicas impactadas" },
];

/**
 * FAQ da LP /feoclinicas (também usada no JSON-LD FAQPage).
 * Fonte de verdade das objeções comerciais — manter alinhada com o discurso
 * da reunião de venda e com /admin/plano-b2b.
 */
export const B2B_FAQ: { q: string; a: string }[] = [
  {
    q: "O que é o FEO para Clínicas?",
    a: "O FEO para Clínicas é uma solução de desenvolvimento técnico para equipes de fisioterapia. Sua clínica passa a ter acesso à estrutura educacional da FEO, com pós-graduações, cursos, discussões clínicas ao vivo e outras ferramentas para apoiar o desenvolvimento contínuo dos fisioterapeutas.\n\nVocê pode escolher entre dar autonomia para que a equipe utilize as formações livremente ou contar com um acompanhamento mais próximo da FEO.",
  },
  {
    q: "Qual é a diferença entre o Plano Livre e o Plano Guiado?",
    a: "No Plano Livre, sua equipe tem acesso às formações e ferramentas da FEO e pode estudar com autonomia.\n\nJá o Plano Guiado inclui tudo do Plano Livre e acrescenta um acompanhamento individualizado: realizamos um diagnóstico da equipe, direcionamos trilhas de estudo para cada fisioterapeuta e fazemos reavaliações periódicas para acompanhar sua evolução.",
  },
  {
    q: "Como funciona o acompanhamento da equipe no Plano Guiado?",
    a: "O primeiro passo é entender o nível atual dos fisioterapeutas e identificar os principais pontos fortes e prioridades de desenvolvimento da equipe.\n\nA partir disso, cada profissional recebe uma trilha de estudos alinhada às suas necessidades e aos objetivos da clínica. Ao longo do programa, novas avaliações ajudam a acompanhar a evolução e ajustar esse direcionamento.",
  },
  {
    q: "O que cada fisioterapeuta da minha equipe terá acesso?",
    a: "Cada profissional terá acesso à estrutura educacional da FEO, que inclui 6 opções de pós-graduação, mais de 120 cursos e formações, discussões clínicas ao vivo, Comunidade FEO, Agente de IA e Roteiro de Avaliação.\n\nNo Plano Guiado, a equipe também conta com diagnóstico, trilhas individuais de desenvolvimento e reavaliações periódicas.",
  },
  {
    q: "Quanto custa e como funciona a contratação?",
    a: "O investimento é calculado de acordo com o número de fisioterapeutas da sua equipe.\n\nO Plano Livre custa R$97/mês por fisioterapeuta e o Plano Guiado R$297/mês por fisioterapeuta, ambos em uma assinatura anual de 12 meses.\n\nVocê pode utilizar o simulador disponível nesta página para visualizar o investimento total para a sua clínica.",
  },
  {
    q: "Como faço para levar a FEO para minha clínica?",
    a: "Escolha o plano mais adequado ao momento da sua clínica ou preencha o formulário de interesse.\n\nNossa equipe entrará em contato pelo WhatsApp para entender melhor o seu cenário, apresentar o funcionamento do programa e orientar os próximos passos.",
  },
  {
    q: "E se um fisioterapeuta sair da minha equipe durante o programa?",
    a: "Sem problema. Caso um profissional deixe a clínica durante o período de contratação, você pode substituí-lo por outro fisioterapeuta da equipe.\n\nAssim, a vaga continua vinculada à clínica e o novo profissional passa a ter acesso à estrutura do FEO para Clínicas, sem que você perca aquela contratação.",
  },
];
